import { connect, FormikValues } from 'formik'
import { get } from 'lodash'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { boolean, string } from 'yup'

import ListBox from '@/components/GenericForm/ListBox'
import { Row } from '@/components/StyledComponents'
import Checkbox from '@/components/TailwindUI/Checkbox'
import { VariableInputContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import VariableInput from '@/pages/SpaceAppBuilder/components/VariableInput/VariableInput'
import { getAppBuilderFeatureData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import { useWorkflowFeature } from '@/pages/SpaceAppBuilder/FeaturesPage/WorkflowFeatureContext'
import { Config, SetConfigVariableStepData, StepData } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { useAppSelector } from '@/store/hooks'

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
//</editor-fold>

//<editor-fold desc="Interfaces">
type SetConfigVariableProps = {
  readOnly?: boolean
  openConfigModal?: () => void
}
//</editor-fold>

const getValidationSchema = () => {
  return {
    configVariable: string().trim().required('Please select a config variable.'),
    configValue: string().trim(),
    shouldAppend: boolean()
  }
}

const getInitialValues = (stepData: SetConfigVariableStepData): StepData => ({
  configVariable: stepData.configVariable,
  configValue: stepData.configValue,
  shouldAppend: stepData.shouldAppend || false
})

const SetConfigVariableOperation = ({
  formik,
  openConfigModal,
  readOnly = false
}: SetConfigVariableProps & FormikValues) => {
  const { getVariablesForStep } = useWorkflowFeature()
  const variables = getVariablesForStep()
  const appFeatureFetchState = useAppSelector(getAppBuilderFeatureData)
  const [configOptions, setConfigOptions] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    if (appFeatureFetchState.feature.config?.length > 0) {
      const defaultValue = { label: 'Select Config Variable', value: '' }
      const options = appFeatureFetchState.feature.config.map((config: Config) => ({
        label: config.name,
        value: config.name
      }))
      setConfigOptions([defaultValue, ...options])
    }
  }, [appFeatureFetchState.feature.config])
  const { touched, errors, values } = formik

  const isSelectedVariableArray = () => {
    return appFeatureFetchState.feature.config.some(
      (option: { name: any; type: string }) => option.name === values.stepData.configVariable && option.type === 'array'
    )
  }
  return (
    <div data-testid="set-config-variable-property-panel" className="mt-4">
      {appFeatureFetchState.feature.config?.length > 0 ? (
        <>
          <StyledRow className="w-full">
            <ListBox
              options={configOptions}
              disabled={readOnly}
              className="mb-2 w-full [&>label]:font-bold"
              label="Variable Name"
              error={!!get(touched, 'stepData.configVariable') && !!get(errors, 'stepData.configVariable')}
              required={true}
              name="stepData.configVariable"
              value={
                configOptions.some(
                  (option: { label: string; value: string }) => option.value === formik.values.stepData.configVariable
                )
                  ? formik.values.stepData.configVariable
                  : formik.setFieldValue(`stepData.configVariable`, '')
              }
              onChange={({ value }) => formik.setFieldValue(`stepData.configVariable`, value)}
            />
          </StyledRow>
          <VariableInputContainer>
            <VariableInput
              label="Value"
              readOnly={readOnly}
              name={'stepData.configValue'}
              variables={variables}
              placeholder={`Enter Value`}
            />
          </VariableInputContainer>
          {isSelectedVariableArray() && (
            <Checkbox
              checked={values.stepData.shouldAppend}
              disabled={readOnly}
              onChange={() => formik.setFieldValue(`stepData.shouldAppend`, !values.stepData.shouldAppend)}
              label="Should append to existing value?"
              description="Will create a new array or append value to the existing array when option is enabled"
            />
          )}
        </>
      ) : (
        <button
          type="button"
          disabled={readOnly}
          className="block py-1.5 px-3 text-center text-sm font-semibold leading-6 text-blue-600 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          onClick={openConfigModal}
        >
          + Config Variable
        </button>
      )}
    </div>
  )
}

export default connect(SetConfigVariableOperation)

export { getInitialValues, getValidationSchema }
